import { create } from 'zustand'

import {
  DocumentPreferences,
  FilterPreferences,
  InfoPanelLayoutPreferences,
  LayoutPreferences,
  ModuleTypeEnum,
  PreferenceContextEnum,
  PreferenceScopeEnum,
  TablePreferences,
  ThemePreferences,
  UserPreferenceData,
  ViewPreferences,
} from '@scrambl/shared'
import { SessionChangedError } from '@scrambl/shared/session-scope'

import { userPreferencesApi } from '../lib/api/user-preferences.api'

// Define the union type for possible preference data structures
export type ConcretePreferenceDetails =
  | TablePreferences
  | ThemePreferences
  | FilterPreferences
  | LayoutPreferences
  | DocumentPreferences
  | ViewPreferences
  | InfoPanelLayoutPreferences
  | Record<string, unknown> // Fallback for other/generic preferences

export interface UserPreferencesState {
  preferences: UserPreferenceData[]
  isLoading: boolean
  isInitialized: boolean
  error: Error | null
  fetchUserPreferences: () => Promise<void>
  getPreference: (
    context: PreferenceContextEnum,
    moduleType?: ModuleTypeEnum,
    scope?: PreferenceScopeEnum,
  ) => UserPreferenceData | undefined
  savePreference: (
    context: PreferenceContextEnum,
    preferenceDetails: ConcretePreferenceDetails, // Use the new union type
    moduleType?: ModuleTypeEnum,
    scope?: PreferenceScopeEnum,
  ) => Promise<UserPreferenceData | undefined>
  /** Merge against the latest successful write, serially within this session. */
  updatePreference: (
    context: PreferenceContextEnum,
    update: (
      current: ConcretePreferenceDetails | undefined,
    ) => ConcretePreferenceDetails,
    moduleType?: ModuleTypeEnum,
    scope?: PreferenceScopeEnum,
  ) => Promise<UserPreferenceData | undefined>
  /**
   * Re-read ONE preference from the server and merge it into the store.
   * Used when a preference changed outside this tab (another device, or a
   * server-side write such as Benny pinning to the sidebar), where the
   * initialized-once fetchUserPreferences would be a no-op.
   */
  syncPreferenceFromServer: (
    context: PreferenceContextEnum,
    moduleType?: ModuleTypeEnum,
    scope?: PreferenceScopeEnum,
  ) => Promise<void>
  resetPreferenceToDefault: (
    context: PreferenceContextEnum,
    moduleType?: ModuleTypeEnum,
  ) => Promise<void>
  clearPreferences: () => void // Action to clear preferences (e.g. on logout)
}

const DEBUG_MODE =
  process.env.NODE_ENV === 'development' &&
  localStorage.getItem('USER_PREFS_STORE_DEBUG') === 'true'

const debugLog = (...args: unknown[]) => {
  if (DEBUG_MODE) {
  }
}

const matchesModuleType = (
  prefModuleType: ModuleTypeEnum | null,
  moduleType?: ModuleTypeEnum,
) =>
  prefModuleType === moduleType ||
  (prefModuleType === null && moduleType === undefined)

const pickLatestPreference = (prefs: UserPreferenceData[]) => {
  if (prefs.length === 0) return undefined
  const sorted = [...prefs].sort((a, b) => {
    const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
    const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
    if (aTime !== bTime) return bTime - aTime
    const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0
    const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0
    return bCreated - aCreated
  })
  return sorted[0]
}

export const getUserAppPreference = (preferences: UserPreferenceData[]) =>
  pickLatestPreference(
    preferences.filter(
      p =>
        p.context === PreferenceContextEnum.APP &&
        p.scope === PreferenceScopeEnum.USER &&
        matchesModuleType(p.moduleType, undefined),
    ),
  )

export const useUserPreferencesStore = create<UserPreferencesState>(
  (set, get) => {
    // Debounce mechanism to prevent rapid successive calls
    let fetchTimeout: NodeJS.Timeout | null = null
    let isCurrentlyFetching = false
    // A request started before an account/workspace reset cannot repopulate it.
    let sessionGeneration = 0
    const updates = new Map<string, Promise<UserPreferenceData | undefined>>()
    const revisions = new Map<string, number>()
    const syncs = new Map<string, number>()
    const preferenceKey = (
      context: PreferenceContextEnum,
      moduleType: ModuleTypeEnum | undefined,
      scope: PreferenceScopeEnum,
    ) => JSON.stringify([context, moduleType, scope])
    const revise = (key: string) =>
      revisions.set(key, (revisions.get(key) ?? 0) + 1)

    return {
      preferences: [],
      isLoading: false,
      isInitialized: false,
      error: null,

      fetchUserPreferences: async () => {
        // Prevent multiple simultaneous fetches
        if (isCurrentlyFetching) {
          debugLog('Fetch already in progress, skipping')
          return
        }

        // If already initialized, don't fetch again
        if (get().isInitialized) {
          debugLog('Preferences already initialized, skipping fetch')
          return
        }

        // Clear any pending fetch timeout
        if (fetchTimeout) {
          clearTimeout(fetchTimeout)
          fetchTimeout = null
        }

        debugLog('Fetching user preferences')
        const requestGeneration = sessionGeneration
        isCurrentlyFetching = true
        set({ isLoading: true, error: null })
        try {
          const response = await userPreferencesApi.getAllUserPreferences()
          if (requestGeneration !== sessionGeneration) return
          const fetchedPreferences = response.data || []
          debugLog('Fetched preferences from API (/all):', fetchedPreferences)
          const dashboardPrefFromAll = fetchedPreferences.find(
            p =>
              p.moduleType === ModuleTypeEnum.TODAY &&
              p.context === PreferenceContextEnum.LAYOUT,
          )
          debugLog(
            'Dashboard preference found in /all response (moduleType: DASHBOARD, context: LAYOUT):',
            dashboardPrefFromAll,
          )

          // Debug: Check for favorites
          const favoritesPref = fetchedPreferences.find(
            p => p.context === PreferenceContextEnum.FAVORITES,
          )
          debugLog(
            'Favorites preference found in /all response:',
            favoritesPref,
          )

          // Debug: Log all contexts
          const contexts = fetchedPreferences.map(p => p.context)
          debugLog('All contexts in preferences:', contexts)

          set({
            preferences: fetchedPreferences,
            isLoading: false,
            isInitialized: true,
          })
        } catch (err: unknown) {
          if (requestGeneration !== sessionGeneration) return
          debugLog('Error fetching preferences:', err)
          set({ error: err as Error, isLoading: false })
        } finally {
          if (requestGeneration === sessionGeneration)
            isCurrentlyFetching = false
        }
      },

      getPreference: (
        context: PreferenceContextEnum,
        moduleType?: ModuleTypeEnum,
        scope?: PreferenceScopeEnum,
      ) => {
        const prefs = get().preferences
        debugLog(
          `Getting preference for context: ${context}, moduleType: ${moduleType}`,
        )
        const matches = (p: UserPreferenceData) =>
          p.context === context && matchesModuleType(p.moduleType, moduleType)

        if (scope === PreferenceScopeEnum.USER) {
          const userPref = pickLatestPreference(
            prefs.filter(
              p => matches(p) && p.scope === PreferenceScopeEnum.USER,
            ),
          )
          if (userPref) {
            debugLog('Found user-specific preference:', userPref)
          }
          return userPref
        }

        if (scope === PreferenceScopeEnum.ORGANIZATION) {
          const orgPref = pickLatestPreference(
            prefs.filter(
              p => matches(p) && p.scope === PreferenceScopeEnum.ORGANIZATION,
            ),
          )
          if (orgPref) {
            debugLog('Found organization-specific preference:', orgPref)
          }
          return orgPref
        }

        const userPref = pickLatestPreference(
          prefs.filter(p => matches(p) && p.scope === PreferenceScopeEnum.USER),
        )
        if (userPref) {
          debugLog('Found user-specific preference:', userPref)
          return userPref
        }

        const orgPref = pickLatestPreference(
          prefs.filter(
            p => matches(p) && p.scope === PreferenceScopeEnum.ORGANIZATION,
          ),
        )
        if (orgPref) {
          debugLog('Found organization-specific preference:', orgPref)
          return orgPref
        }

        debugLog('No preference found for context:', context, moduleType)
        return undefined
      },

      savePreference: async (
        context: PreferenceContextEnum,
        newPreferenceDetails: ConcretePreferenceDetails,
        moduleType?: ModuleTypeEnum,
        scope: PreferenceScopeEnum = PreferenceScopeEnum.USER,
      ) => {
        const requestGeneration = sessionGeneration
        const key = preferenceKey(context, moduleType, scope)
        revise(key)
        debugLog(
          `Saving preference for context: ${context}, moduleType: ${moduleType}, scope: ${scope}`,
          newPreferenceDetails,
        )
        // Don't set loading state for preference saves to avoid UI flickering
        try {
          // Cast the preference details to Record<string, unknown> to match the API signature
          const savedPref = await userPreferencesApi.saveUserPreferences(
            context,
            newPreferenceDetails as Record<string, unknown>,
            moduleType,
            scope,
          )
          if (requestGeneration !== sessionGeneration) return undefined
          revise(key)

          if (savedPref) {
            debugLog(
              'Successfully saved preference, updating store:',
              savedPref,
            )
            set(state => {
              const updatedPreferences = state.preferences.filter(p => {
                if (
                  !(
                    p.context === context &&
                    matchesModuleType(p.moduleType, moduleType) &&
                    p.scope === scope
                  )
                ) {
                  return true
                }
                if (scope === PreferenceScopeEnum.USER) {
                  return p.userId !== savedPref.userId
                }
                return false
              })
              updatedPreferences.push(savedPref)
              debugLog(
                'Local store preferences array AFTER push:',
                updatedPreferences,
              )
              const dashboardPrefAfterSave = updatedPreferences.find(
                p =>
                  p.moduleType === ModuleTypeEnum.TODAY &&
                  p.context === PreferenceContextEnum.LAYOUT,
              )
              debugLog(
                'Dashboard preference in local store AFTER save (moduleType: DASHBOARD, context: LAYOUT):',
                dashboardPrefAfterSave,
              )
              return { preferences: updatedPreferences, error: null } // Only update preferences and clear errors
            })
            return savedPref
          }
          debugLog('Save preference did not return data.')
          return undefined
        } catch (err: unknown) {
          debugLog('Error saving preference:', err)
          if (requestGeneration === sessionGeneration)
            set({ error: err as Error })
          throw err
        }
      },

      updatePreference: (
        context,
        update,
        moduleType,
        scope = PreferenceScopeEnum.USER,
      ) => {
        const generation = sessionGeneration
        const key = preferenceKey(context, moduleType, scope)
        const previous = updates.get(key) ?? Promise.resolve()
        const pending = previous
          .catch(() => undefined)
          .then(async () => {
            if (generation !== sessionGeneration)
              throw new SessionChangedError()
            if (!get().isInitialized)
              throw new Error('Preferences are still loading. Try again.')
            const current = get().getPreference(
              context,
              moduleType,
              scope,
            )?.preferences
            const result = await get().savePreference(
              context,
              update(current),
              moduleType,
              scope,
            )
            if (generation !== sessionGeneration)
              throw new SessionChangedError()
            if (!result)
              throw new Error('Preferences could not be saved. Try again.')
            return result
          })
          .finally(() => {
            if (updates.get(key) === pending) updates.delete(key)
          })
        updates.set(key, pending)
        return pending
      },

      syncPreferenceFromServer: async (
        context: PreferenceContextEnum,
        moduleType?: ModuleTypeEnum,
        scope: PreferenceScopeEnum = PreferenceScopeEnum.USER,
      ) => {
        const generation = sessionGeneration
        const key = preferenceKey(
          context,
          moduleType,
          scope ?? PreferenceScopeEnum.USER,
        )
        const revision = revisions.get(key)
        const sync = (syncs.get(key) ?? 0) + 1
        syncs.set(key, sync)
        try {
          const serverPref = await userPreferencesApi.getUserPreferences(
            context,
            moduleType,
            scope,
          )
          if (
            generation !== sessionGeneration ||
            revision !== revisions.get(key) ||
            sync !== syncs.get(key)
          )
            return
          set(state => {
            const withoutStale = state.preferences.filter(
              p =>
                !(
                  p.context === context &&
                  matchesModuleType(p.moduleType, moduleType) &&
                  p.scope === scope
                ),
            )
            return {
              preferences: serverPref
                ? [...withoutStale, serverPref]
                : withoutStale,
            }
          })
        } catch (err: unknown) {
          debugLog('Error syncing preference from server:', err)
        }
      },

      resetPreferenceToDefault: async (
        context: PreferenceContextEnum,
        moduleType?: ModuleTypeEnum,
      ) => {
        const generation = sessionGeneration
        const key = preferenceKey(context, moduleType, PreferenceScopeEnum.USER)
        revise(key)
        debugLog(
          `Resetting preference to default for context: ${context}, moduleType: ${moduleType}`,
        )
        set({ isLoading: true })
        try {
          await userPreferencesApi.resetUserPreferences(context, moduleType)
          if (generation !== sessionGeneration) return
          revise(key)
          debugLog('Successfully reset preference on backend, updating store.')
          set(state => ({
            preferences: state.preferences.filter(
              p =>
                !(
                  p.context === context &&
                  matchesModuleType(p.moduleType, moduleType) &&
                  p.scope === PreferenceScopeEnum.USER
                ), // only remove user-scoped pref for this context/module
            ),
            isLoading: false,
            error: null, // Clear error on success
          }))
        } catch (err: unknown) {
          debugLog('Error resetting preference:', err)
          if (generation === sessionGeneration)
            set({ error: err as Error, isLoading: false })
          throw err
        }
      },
      clearPreferences: () => {
        sessionGeneration += 1
        updates.clear()
        revisions.clear()
        syncs.clear()
        debugLog('Clearing all preferences from the store.')
        set({
          preferences: [],
          isLoading: false,
          error: null,
          isInitialized: false,
        })
        isCurrentlyFetching = false // Reset fetching state
        if (fetchTimeout) {
          clearTimeout(fetchTimeout)
          fetchTimeout = null
        }
      },
    }
  },
)
